import type { MigrationPlanDTO, VpsDTO } from '@devrobot/shared';
import { CircleAlert, CircleCheck, Info, TriangleAlert } from 'lucide-react';
import { useEnvironments, useVpsList } from '../../../api/queries';
import { ErrorAlert } from '../../../components/ErrorAlert';
import { SkeletonLines } from '../../../components/Skeleton';
import { StatusBadge } from '../../../components/StatusBadge';
import { formatBytes } from '../../../lib/format';
import { KindBadge } from '../PlanSummary';
import { isSameTarget, kindOf, type StepProps } from './model';

export const MIGRATE_CAPABILITY = 'migrate.v1';
export const supportsMigration = (v: Pick<VpsDTO, 'agent'>) => v.agent.capabilities.includes(MIGRATE_CAPABILITY);

function Check({ ok, warn, children }: { ok: boolean; warn?: boolean; children: React.ReactNode }) {
  const Icon = ok ? CircleCheck : warn ? TriangleAlert : CircleAlert;
  const cls = ok ? 'dr-ink-success' : warn ? 'dr-ink-warning' : 'dr-ink-error';
  return (
    <li className="flex items-start gap-2 text-sm">
      <Icon className={`mt-0.5 size-4 shrink-0 ${cls}`} aria-hidden="true" />
      <div className="min-w-0 flex-1">{children}</div>
    </li>
  );
}

/** Destination facts from the server preview. */
export function DestinationFacts({ plan }: { plan: MigrationPlanDTO }) {
  const d = plan.destination;
  const disk = plan.disk;
  const diskShort = disk.requiredBytes !== null && disk.destinationFreeBytes !== null && disk.requiredBytes > disk.destinationFreeBytes;
  return (
    <ul className="space-y-2.5">
      <Check ok warn={false}>
        {d.exists ? (
          <>
            A target for <span className="font-medium">{d.environment}</span> already exists on {d.vps.name} ({d.deployed ? 'deployed' : 'not deployed'}
            {d.release ? `, ${d.release.branch}@${d.release.shortSha}` : ''}).
          </>
        ) : (
          <>
            A new target <span className="font-mono">{d.stack}</span> will be created on {d.vps.name}.
          </>
        )}
      </Check>
      {d.hasData ? (
        <Check ok={false} warn>
          The destination already holds data{d.existingVolumes.length ? ` (${d.existingVolumes.join(', ')})` : ''}. Copying replaces it; you will be asked to confirm.
        </Check>
      ) : (
        <Check ok>The destination holds no data for this application.</Check>
      )}
      <Check ok={plan.agents.source.online && plan.agents.source.supported}>
        Source agent: {plan.agents.source.online ? 'online' : 'offline'}, {plan.agents.source.supported ? 'supports migrations' : 'needs an upgrade to support migrations'}
      </Check>
      <Check ok={plan.agents.destination.online && plan.agents.destination.supported}>
        Destination agent: {plan.agents.destination.online ? 'online' : 'offline'}, {plan.agents.destination.supported ? 'supports migrations' : 'needs an upgrade to support migrations'}
      </Check>
      <Check ok={!diskShort} warn={disk.destinationFreeBytes === null || disk.requiredBytes === null}>
        Disk: needs {disk.requiredBytes === null ? 'an unknown amount' : formatBytes(disk.requiredBytes)}; destination has {disk.destinationFreeBytes === null ? 'an unknown amount' : formatBytes(disk.destinationFreeBytes)} free
      </Check>
    </ul>
  );
}

export function DestinationStep({ state, dispatch, preview }: StepProps) {
  const envs = useEnvironments(state.project, Boolean(state.project));
  const vpsList = useVpsList();
  const kind = kindOf(state);
  const same = isSameTarget(state);
  const plan = preview.plan;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <fieldset className="fieldset">
          <label className="fieldset-legend" htmlFor="migration-dest-env">
            Destination environment
          </label>
          {envs.isPending ? <div className="skeleton h-10 w-full" /> : null}
          <ErrorAlert error={envs.error} onRetry={() => void envs.refetch()} />
          {envs.data ? (
            <select id="migration-dest-env" className="select w-full" value={state.destEnvironment} onChange={(e) => dispatch({ type: 'set-destination', environment: e.target.value })}>
              <option value="">Choose an environment…</option>
              {envs.data.map((e) => (
                <option key={e.name} value={e.name}>
                  {e.name}
                  {e.name === state.sourceEnvironment ? ' (same environment: move to another VPS)' : ''}
                  {e.protected ? ' · protected' : ''}
                </option>
              ))}
            </select>
          ) : null}
          <p className="label whitespace-normal">The same environment moves the deployment to another VPS; another environment copies the data into it.</p>
        </fieldset>

        <fieldset className="fieldset">
          <label className="fieldset-legend" htmlFor="migration-dest-vps">
            Destination VPS
          </label>
          {vpsList.isPending ? <div className="skeleton h-10 w-full" /> : null}
          <ErrorAlert error={vpsList.error} onRetry={() => void vpsList.refetch()} />
          {vpsList.data ? (
            <select id="migration-dest-vps" className="select w-full" value={state.destVpsId} onChange={(e) => dispatch({ type: 'set-destination', vpsId: e.target.value })}>
              <option value="">Choose a VPS…</option>
              {vpsList.data.map((v) => (
                <option key={v.id} value={v.id} disabled={state.destEnvironment === state.sourceEnvironment && v.id === state.sourceVpsId}>
                  {v.name} · {v.connection}
                  {supportsMigration(v) ? '' : ' · agent upgrade needed'}
                  {v.id === state.sourceVpsId ? ' (current VPS)' : ''}
                </option>
              ))}
            </select>
          ) : null}
        </fieldset>
      </div>

      {state.destVpsId && vpsList.data ? <VpsCard vps={vpsList.data.find((v) => v.id === state.destVpsId)} /> : null}

      {kind && state.destVpsId && !same ? (
        <p className="flex flex-wrap items-center gap-2 text-sm">
          This is a <KindBadge kind={kind} />
          <span className="text-base-content/65">{kind === 'server-move' ? 'The same environment keeps its configuration and moves to another VPS.' : 'Data and selected configuration are copied into another environment.'}</span>
        </p>
      ) : null}

      {same ? (
        <div role="alert" className="alert alert-error alert-soft text-sm">
          <CircleAlert className="size-4 shrink-0" aria-hidden="true" />
          <span>The destination is the source itself. Choose another VPS or another environment.</span>
        </div>
      ) : null}

      {state.destEnvironment && state.destVpsId && !same ? (
        <section aria-label="Destination check" className="rounded-field border border-hairline p-4">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
            Destination check {preview.fetching ? <span className="loading loading-spinner loading-xs" aria-label="Checking the destination" /> : null}
          </h3>
          {preview.error ? <ErrorAlert error={preview.error} title="The destination could not be checked" onRetry={preview.refetch} /> : null}
          {!plan && !preview.error ? <SkeletonLines lines={4} /> : null}
          {plan ? (
            <div className={`space-y-3 transition-opacity ${preview.upToDate ? '' : 'opacity-60'}`}>
              <DestinationFacts plan={plan} />
              {plan.blockers.length > 0 ? (
                <div role="alert" className="alert alert-error alert-soft items-start text-sm">
                  <CircleAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                  <ul className="list-disc pl-4">
                    {plan.blockers.map((b, i) => (
                      <li key={i} className="break-anywhere">
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}

function VpsCard({ vps }: { vps: VpsDTO | undefined }) {
  if (!vps) return null;
  const ok = supportsMigration(vps);
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="font-medium">{vps.name}</span>
      <StatusBadge kind="connection" value={vps.connection} size="xs" />
      {ok ? (
        <span className="badge badge-soft badge-success badge-xs gap-1">
          <CircleCheck className="size-3" aria-hidden="true" /> {MIGRATE_CAPABILITY}
        </span>
      ) : (
        <span className="badge badge-soft badge-warning badge-xs gap-1">
          <TriangleAlert className="size-3" aria-hidden="true" /> no {MIGRATE_CAPABILITY}
        </span>
      )}
      {vps.agent.version ? <span className="text-xs text-base-content/60">agent {vps.agent.version}</span> : null}
      {!ok ? (
        <span className="flex items-center gap-1 text-xs text-base-content/65">
          <Info className="size-3.5" aria-hidden="true" /> Upgrade the agent on this VPS before migrating to it.
        </span>
      ) : null}
    </div>
  );
}
